import type { MaybeRefOrGetter, Ref, WritableComputedRef } from 'vue';
import type { NumberInputBounds, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { getInteger64InputProps, isPrimitiveTypeInteger64, parseInteger64Text, snapInteger64ToStep } from '#layers/data-model/public';

/**
 * Edits a 64-bit field as decimal text: `inputProps` for the string-backed decimal input (unset
 * for other fields), and a model that snaps committed text to the configured step, as integer
 * number inputs always do.
 */
export function useInteger64FormFieldInput(
	model: WritableComputedRef<unknown>,
	field: Ref<ResolvedMonospacePrimitiveField | undefined>,
	bounds: MaybeRefOrGetter<NumberInputBounds>,
) {
	const integer64Type = computed(() => {
		const type = field.value?.type;
		return type && isPrimitiveTypeInteger64(type) ? type : undefined;
	});

	const inputProps = computed(() => integer64Type.value && getInteger64InputProps(integer64Type.value.name, toValue(bounds)));

	const inputModel = computed({
		get: () => model.value,
		set: (value: string | null) => {
			const name = integer64Type.value?.name;
			const parsed = value === null || !name ? undefined : parseInteger64Text(value, name);
			model.value = parsed === undefined || !name ? value : snapInteger64ToStep(parsed, name, toValue(bounds));
		},
	});

	return { inputProps, inputModel };
}
